/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * PERSON / COMPANY NAME MATCHING — the shared source of truth (task_a7e72816).
 *
 * Moved here from api/_person-match.js so both the client (react-scripts, which
 * may only import from src/) and the api layer use the SAME matcher. api/
 * _person-match.js now re-exports these; src/MLRODashboard.jsx keeps its own
 * documented mirror. If you change the rules here, change that mirror too.
 *
 * These tolerate the ways one person's name legitimately varies across a
 * registry, a passport and a typed form (an added or dropped middle name,
 * surname-first ordering, an initial for a first name, punctuation, accents),
 * without merging a genuinely different surname. Company names are compared only
 * after the legal suffix and registration noise are stripped.
 */

// Transliterations that are the SAME name written two ways (ä/ae, ö/oe, …).
// Expanded BEFORE the accent strip, which would otherwise leave a bare 'a'.
const FOLD = [[/ä/g, 'ae'], [/ö/g, 'oe'], [/ü/g, 'ue'], [/ß/g, 'ss'], [/æ/g, 'ae'], [/ø/g, 'oe'], [/å/g, 'aa']];

export const normName = n => {
  let x = String(n || '').toLowerCase();
  for (const [re, to] of FOLD) x = x.replace(re, to);
  x = x.normalize('NFD').replace(/[̀-ͯ]/g, '');
  return x.replace(/[^a-z\s]/g, '').replace(/\s+/g, ' ').trim();
};
// v1_852: the same name with its accents stripped, no transliteration (ü to u).
// Letters with no accent to strip are written plainly (ø to o, æ to ae).
const PLAIN_FOLD = [[/ø/g, 'o'], [/æ/g, 'ae'], [/œ/g, 'oe'], [/ß/g, 'ss'], [/ł/g, 'l'], [/[đð]/g, 'd'], [/þ/g, 'th']];
const plainName = n => { let x = String(n || '').toLowerCase(); for (const [re, to] of PLAIN_FOLD) x = x.replace(re, to); return x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z\s]/g, '').replace(/\s+/g, ' ').trim(); };

// A shared first token and legal suffix must not make two companies one; two
// companies are the same only when their names agree once the suffix and
// registration noise are stripped.
const CO_TOKEN = /\b(ltd|limited|plc|llp|llc|inc|incorporated|corp|corporation|company|holdings?|group|gmbh|mbh|ag|sa|sarl|sas|bv|nv|oy|ab|aps|as|kft|sro|se|kg|ug|pte|pty|trust|foundation|nominees?|partners|lp|kk|srl|spa)\b/;
const CO_NOISE = /\b(ltd|limited|plc|llp|llc|inc|incorporated|corp|corporation|gmbh|mbh|ag|sa|sarl|sas|bv|nv|oy|ab|aps|as|kft|sro|se|kg|ug|pte|pty|lp|kk|srl|spa|company|number|registration|reg|no|the)\b/g;

export function sameCompany(a, b) {
  const k = (v) => normName(String(v == null ? '' : v).replace(/\([^)]*\)/g, ' ').replace(/[0-9]/g, ' ')).replace(CO_NOISE, ' ').replace(/\s+/g, ' ').trim();
  const x = k(a), y = k(b);
  return !!x && !!y && x === y;
}

// v1_812: a title before a name is not part of it. "Mr Steven Anthony Brodie"
// read as a different person from "Steven Brodie" because the first token
// compared was "mr", not "steven" (Fig Markets, production 25/09/26). Leading
// honorifics are dropped before the comparison, only while a name remains.
const HONORIFIC = new Set(['mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'prof', 'sir', 'dame', 'herr', 'frau', 'mme', 'mlle']);
const dropHonorifics = (n) => { const t = n.split(' '); while (t.length > 1 && HONORIFIC.has(t[0])) t.shift(); return t.join(' '); };
function sameNormalised(x, y, a, b) {
  if (!x || !y) return false;
  if (x === y) return true;
  if (CO_TOKEN.test(x) || CO_TOKEN.test(y)) return sameCompany(a, b);
  // One name contained in the other: "Mike Okhemesimi" within
  // "Mike Imonikhu Okhemesimi".
  if (x.includes(y) || y.includes(x)) return true;
  const xs = x.split(' '), ys = y.split(' ');
  // First and last agree, middle names differ or are absent.
  if (xs.length > 1 && ys.length > 1 && xs[0] === ys[0] && xs[xs.length - 1] === ys[ys.length - 1]) return true;
  // Same tokens in a different order (surname-first).
  if (xs.length > 1 && xs.length === ys.length && [...xs].sort().join(' ') === [...ys].sort().join(' ')) return true;
  // An initial against a full first name with the same surname ("H. Nold" is
  // "Hans Nold"), only when every other token agrees.
  if (xs.length === ys.length && xs.length > 1) {
    const initialMatch = (p, q) => (p.length === 1 && q.startsWith(p)) || (q.length === 1 && p.startsWith(q)) || p === q;
    if (xs[xs.length - 1] === ys[ys.length - 1] && xs.slice(0, -1).every((t, i) => initialMatch(t, ys[i]))) return true;
  }
  // Initials with an extra middle initial against a shorter full name (v1_760):
  // "T W Ashcombe" is "Tobias Ashcombe". Same surname, every given name of the
  // shorter agrees in order (an initial matches its full name), every extra
  // given name in the longer is an initial, and at least one initial is in
  // play. The register named the director "T W Ashcombe" and the contact was
  // "Tobias Ashcombe": read as two people on one inbox, the pair fired a false
  // sharedContactChannel High escalation (end to end walk runs 3 and 6).
  // Ambiguity accepted: "T W Ashcombe" also matches "Thomas Ashcombe", as the
  // equal length rule already accepts "J Smith" for "Jane Smith".
  if (xs.length !== ys.length && Math.min(xs.length, ys.length) > 1 && xs[xs.length - 1] === ys[ys.length - 1]) {
    const [lo, hi] = xs.length < ys.length ? [xs, ys] : [ys, xs];
    const im = (p, q) => (p.length === 1 && q.startsWith(p)) || (q.length === 1 && p.startsWith(q)) || p === q;
    const loGiven = lo.slice(0, -1), hiGiven = hi.slice(0, -1);
    if (loGiven.every((t, i) => im(t, hiGiven[i])) && hiGiven.slice(loGiven.length).every(t => t.length === 1)
      && (loGiven.some(t => t.length === 1) || hiGiven.some(t => t.length === 1))) return true;
  }
  // v1_852: surname first with an extra middle name or initial: "Malik
  // Mahsood" is "Mahsood H. Malik". The longer name's first and last words are
  // both in the shorter (each exactly), every word of the shorter is in the
  // longer, and the longer's extra words are its middle names. Equal length
  // reordering is the rule above. Production 28/09/26: the contact "Malik
  // Mahsood" and the owner "Mahsood H. Malik" on one inbox read as two people
  // and fired a false sharedContactChannel High escalation.
  if (xs.length !== ys.length && Math.min(xs.length, ys.length) > 1) {
    const [lo, hi] = xs.length < ys.length ? [xs, ys] : [ys, xs];
    const loSet = new Set(lo);
    if (lo.every(t => t.length > 1 && hi.includes(t)) && loSet.size === lo.length && loSet.has(hi[0]) && loSet.has(hi[hi.length - 1]) && hi[0] !== hi[hi.length - 1]) return true;
  }
  return false;
}

// v1_852: accents compared both ways. "Jürgen Müller" transliterates to
// "juergen mueller" (the register's spelling of the same name) and strips to
// "jurgen muller" (the typed form without the accents); a name matches if
// either reading matches. Until v1_852 only the transliteration was compared, so
// "Jürgen Müller" and "Jurgen Muller" on one inbox read as two people and fired
// a false sharedContactChannel escalation (harness-interview-escalations).
// v1_883: two names that look alike but are two people. A generational
// suffix (Jr, Junior, Sr, Senior, II, III, IV) is part of who the person is:
// "John Smith Jr" is never "John Smith", nor "John Smith Sr". And where both
// names give a middle name or initial, those must agree: "John Michael Smith"
// and "John David Smith" (or "John M. Smith" and "John D. Smith") are two
// people; "John Michael Smith" and "John M. Smith" may be one; a name with no
// middle name ("Steven Brodie") still matches one with ("Steven Anthony
// Brodie"). A father and son, or two relatives, can hold shares in one company.
const GEN_SUFFIX = { jr: 'jr', junior: 'jr', sr: 'sr', senior: 'sr', ii: 'ii', iii: 'iii', iv: 'iv' };
function splitSuffix(n) {
  const t = n.split(' ').filter(Boolean), s = [];
  while (t.length > 2 && GEN_SUFFIX[t[t.length - 1]]) s.unshift(GEN_SUFFIX[t.pop()]);
  return { name: t.join(' '), suffix: s.join(' ') };
}
function middlesConflict(x, y) {
  const xs = x.split(' '), ys = y.split(' ');
  if (xs.length < 3 || ys.length < 3) return false;
  const im = (p, q) => (p.length === 1 && q.startsWith(p)) || (q.length === 1 && p.startsWith(q)) || p === q;
  if (xs[xs.length - 1] !== ys[ys.length - 1] || !im(xs[0], ys[0])) return false;
  const mx = xs.slice(1, -1), my = ys.slice(1, -1);
  for (let i = 0; i < Math.min(mx.length, my.length); i++) if (!im(mx[i], my[i])) return true;
  return false;
}
function samePersonOn(x, y, a, b) {
  const sx = splitSuffix(x), sy = splitSuffix(y);
  if (sx.suffix !== sy.suffix) return false;
  if (middlesConflict(sx.name, sy.name)) return false;
  return sameNormalised(sx.name, sy.name, a, b);
}

export function samePerson(a, b) {
  if (samePersonOn(dropHonorifics(normName(a)), dropHonorifics(normName(b)), a, b)) return true;
  return samePersonOn(dropHonorifics(plainName(a)), dropHonorifics(plainName(b)), a, b);
}


// v1_852: ONE PERSON ON ONE EMAIL ADDRESS. Two entries carrying the same email
// address are one person when their names are close: a title (Dr.), accents,
// reversed order, a middle name or initial (all of which samePerson reads), or
// one letter of transliteration in one word ("Abdulla" and "Abdullah",
// "Mohamed" and "Mohammed", "Yusuf" and "Yousuf"). Production 28/09/26: the
// board chair listed as "Dr. Abdulla Al Nasser" and "Abdullah Al Nasser", same
// role, same email, read as two people; that fired a false sharedContactChannel
// High escalation and sent the chair two identity links. Names that are not
// close on one address stay two people (the escalation). The one letter rule
// is never applied without the shared address.
//
// One letter of transliteration: the longer word is the shorter (at least four
// letters) with one letter added, and that letter is an h anywhere, a doubled
// letter, or a vowel inside the word (never first or last, so "Louis" and
// "Louise", "Paul" and "Paula" stay two). A changed letter is never one
// ("Mark" and "Mary").
const VOWEL = /[aeiouy]/;
function oneLetterAdded(s, l) {
  if (s.length < 4 || l.length !== s.length + 1) return false;
  let i = 0;
  while (i < s.length && s[i] === l[i]) i++;
  if (s.slice(i) !== l.slice(i + 1)) return false;
  const c = l[i];
  if (c === 'h') return true;
  if (l[i - 1] === c || l[i + 1] === c) return true;
  return VOWEL.test(c) && i > 0 && i < l.length - 1;
}

// Close names, for two entries already known to share an address.
export function closeNames(a, b) {
  if (samePerson(a, b)) return true;
  for (const norm of [normName, plainName]) {
    const x = dropHonorifics(norm(a)).split(' ').filter(Boolean);
    const y = dropHonorifics(norm(b)).split(' ').filter(Boolean);
    for (let i = 0; i < x.length; i++) for (let j = 0; j < y.length; j++) {
      const [s, l] = x[i].length < y[j].length ? [x[i], y[j]] : [y[j], x[i]];
      if (!oneLetterAdded(s, l)) continue;
      const y2 = y.slice(); y2[j] = x[i];
      if (samePersonOn(x.join(' '), y2.join(' '), a, b)) return true;
    }
  }
  return false;
}

// The address as the escalation reads it: lower case, a +tag dropped.
export const normEmail = (e) => { const p = String(e || '').toLowerCase().trim().split('@'); return p.length === 2 && p[0] && p[1] ? p[0].split('+')[0] + '@' + p[1] : null; };

// Two entries ({ name, email } objects or bare names) are one person on the
// file: the name matcher says so, or both carry the same address and the names
// are close. `match` is the caller's name matcher (the file aware one where the
// caller has it).
const entryNameOf = (p) => (p && typeof p === 'object') ? p.name : String(p || '').split(' — ')[0];
export function sameEntryPerson(a, b, match = samePerson) {
  const na = entryNameOf(a), nb = entryNameOf(b);
  if (!na || !nb) return false;
  if (match(na, nb)) return true;
  const ea = normEmail(a && a.email), eb = normEmail(b && b.email);
  return !!ea && ea === eb && closeNames(na, nb);
}
